import React, { useState, useMemo } from "react";
import { Button, Input } from "reactstrap";
import { useTrialsData } from "./hooks/useTrialsData";
import TrialsStats from "./components/TrialsStats";
import TrialsTable from "./components/TrialsTable";
import { Breadcrumb } from "../../../components/Common";
import { toISODate, addDays, getStartOfWeek } from "../../../utils/date";

const TrialsPage = () => {
    document.title = "Aulas Experimentais | NeoApp - Sistema de Gestão";

    // ─── Período ───
    const today = new Date();
    const [preset, setPreset] = useState("MONTH");
    
    // Inicializar com o mês atual
    const [startDate, setStartDate] = useState(() => {
        const y = today.getFullYear();
        const m = today.getMonth();
        return toISODate(new Date(y, m, 1));
    });
    const [endDate, setEndDate] = useState(() => {
        const y = today.getFullYear();
        const m = today.getMonth();
        return toISODate(new Date(y, m + 1, 0));
    });

    // ─── Filtros client-side ───
    const [filterEmployee, setFilterEmployee] = useState("");
    const [filterConsultant, setFilterConsultant] = useState("");

    const { trials, loading, error } = useTrialsData(startDate, endDate);

    // ─── Extrair listas únicas para os selects ───
    const employees = useMemo(() => {
        const names = [...new Set(trials.map(t => t.employeeName).filter(Boolean))];
        return names.sort();
    }, [trials]);

    const consultants = useMemo(() => {
        const names = [...new Set(trials.map(t => t.consultantName).filter(Boolean))];
        return names.sort();
    }, [trials]);

    // ─── Filtrar trials ───
    const filteredTrials = useMemo(() => {
        let result = trials;
        if (filterEmployee) {
            result = result.filter(t => t.employeeName === filterEmployee);
        }
        if (filterConsultant) {
            result = result.filter(t => t.consultantName === filterConsultant);
        }
        return result;
    }, [trials, filterEmployee, filterConsultant]);

    // ─── Presets de período ───
    const handlePresetChange = (newPreset) => {
        setPreset(newPreset);
        const now = new Date();
        if (newPreset === "TODAY") {
            setStartDate(toISODate(now));
            setEndDate(toISODate(now));
        } else if (newPreset === "WEEK") {
            const startW = getStartOfWeek(now);
            const endW = addDays(startW, 6);
            setStartDate(toISODate(startW));
            setEndDate(toISODate(endW));
        } else if (newPreset === "MONTH") {
            const y = now.getFullYear();
            const m = now.getMonth();
            setStartDate(toISODate(new Date(y, m, 1)));
            setEndDate(toISODate(new Date(y, m + 1, 0)));
        }
    };

    const handleDateChange = (field, val) => {
        setPreset("CUSTOM");
        if (field === "start") setStartDate(val);
        else setEndDate(val);
    };

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Aulas Experimentais" />
            </div>

            <div className="border-top mb-3"></div>

            <div className="trials-container mt-3">
                {/* Descrição e Período */}
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-4 gap-3">
                    <p className="text-muted mb-0">
                        Gestão, métricas de conversão e comparecimento de aulas experimentais.
                    </p>

                            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                                <div className="btn-group rounded-0">
                                    <Button
                                        color={preset === "TODAY" ? "primary" : "white"}
                                        onClick={() => handlePresetChange("TODAY")}
                                        className="border rounded-0"
                                    >
                                        Hoje
                                    </Button>
                                    <Button
                                        color={preset === "WEEK" ? "primary" : "white"}
                                        onClick={() => handlePresetChange("WEEK")}
                                        className="border rounded-0"
                                    >
                                        Semana
                                    </Button>
                                    <Button
                                        color={preset === "MONTH" ? "primary" : "white"}
                                        onClick={() => handlePresetChange("MONTH")}
                                        className="border rounded-0"
                                    >
                                        Mês
                                    </Button>
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                    <Input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => handleDateChange("start", e.target.value)}
                                        className="border-0 bg-light rounded-0"
                                        style={{ maxWidth: "140px" }}
                                    />
                                    <span className="text-muted px-1">até</span>
                                    <Input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => handleDateChange("end", e.target.value)}
                                        className="border-0 bg-light rounded-0"
                                        style={{ maxWidth: "140px" }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Stats (usa dados filtrados) */}
                        <TrialsStats trials={filteredTrials} />

                        {/* Filtros de Professor e Consultor */}
                        <div className="row g-2 mb-4">
                            <div className="col-sm-6 col-lg-3">
                                <label className="form-label font-size-13 text-muted mb-1">Professor</label>
                                <Input
                                    type="select"
                                    bsSize="sm"
                                    value={filterEmployee}
                                    onChange={(e) => setFilterEmployee(e.target.value)}
                                    className="bg-light border-0 rounded-0"
                                >
                                    <option value="">Todos os professores</option>
                                    {employees.map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </Input>
                            </div>
                            <div className="col-sm-6 col-lg-3">
                                <label className="form-label font-size-13 text-muted mb-1">Consultor(a)</label>
                                <Input
                                    type="select"
                                    bsSize="sm"
                                    value={filterConsultant}
                                    onChange={(e) => setFilterConsultant(e.target.value)}
                                    className="bg-light border-0 rounded-0"
                                >
                                    <option value="">Todos os consultores</option>
                                    {consultants.map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </Input>
                            </div>
                            {(filterEmployee || filterConsultant) && (
                                <div className="col-auto d-flex align-items-end">
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="text-danger p-0"
                                        onClick={() => { setFilterEmployee(""); setFilterConsultant(""); }}
                                    >
                                        <i className="bx bx-x me-1"></i>Limpar filtros
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Table */}
                        <div className="mt-4">
                            {error ? (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                    <br /><br />
                                    <small>Consulte o console do navegador ou execute o comando deploy firestore:indexes para gerar os índices necessários.</small>
                                </div>
                            ) : (
                                <TrialsTable trials={filteredTrials} loading={loading} />
                            )}
                        </div>
            </div>
        </React.Fragment>
    );
};

export default TrialsPage;
